import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Alert, Button, Card, Col, Form, Row, Spinner} from 'react-bootstrap';
import {useNavigate} from 'react-router-dom';

import AppContext from 'context/Context';
import Flex from 'components/common/Flex';
import {PromptDeleteApi, PromptEditApi, PromptSaveApi, PromptViewApi} from '../../../api/prompt_api';

const DEFAULT_TARGET_OUTCOME = 'all';

const PromptDashboard = ({
  promptViewApi_ = PromptViewApi,
  promptSaveApi_ = PromptSaveApi,
  promptEditApi_ = PromptEditApi,
  promptDeleteApi_ = PromptDeleteApi,
  errorLog_ = console.log
}) => {
  const navigate = useNavigate();
  const {
    config: { customerCode, apiUrl },
    setConfig
  } = useContext(AppContext);

  const [prompts, setPrompts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [deletingRevisionUuid, setDeletingRevisionUuid] = useState('');
  const [deletingPromptName, setDeletingPromptName] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [expandedRevisionUuid, setExpandedRevisionUuid] = useState('');
  const [promptName, setPromptName] = useState('');
  const [promptText, setPromptText] = useState('');
  const [targetOutcome, setTargetOutcome] = useState(DEFAULT_TARGET_OUTCOME);
  const [tags, setTags] = useState('');
  const [sequence, setSequence] = useState('');
  const [headless, setHeadless] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const listItems = useMemo(() => {
    return Object.keys(prompts)
      .map(name => {
        const revisions = prompts[name] || [];
        const latest = revisions.length ? revisions[revisions.length - 1] : {};
        return {
          name,
          sequence: latest.sequence,
          latestRevisionUuid: latest.revisionUuid || '',
          latestTimestamp: latest.timestamp || 0,
          latestPrompt: latest.prompt || '',
          revisionCount: revisions.length
        };
      }).sort((a, b) => (a.sequence || Number.MAX_SAFE_INTEGER) -
        (b.sequence || Number.MAX_SAFE_INTEGER) || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  }, [prompts]);

  const loadPrompts = () => {
    if (!customerCode || customerCode === 'demo') {
      return;
    }
    if (isMounted.current) {
      setLoading(true);
    }
    const data = {token: customerCode};
    promptViewApi_(apiUrl).fetch({data})
      .then(response => {
        if (!isMounted.current) return;
        const toSet = response && response.prompts ? response.prompts : {};
        setPrompts(toSet);
        setConfig('isPromptAdmin', true);
        setLoading(false);
      })
      .catch(error => {
        if (!isMounted.current) return;
        setConfig('isPromptAdmin', false);
        setLoading(false);
        errorLog_(error);
        navigate('/errors/404');
      });
  };

  useEffect(() => {
    if (customerCode === '') {
      navigate('/');
      return;
    }
    if (customerCode === 'demo') {
      navigate('/errors/404');
      return;
    }
    loadPrompts();
    // Loading is intentionally keyed only to the active customer and API endpoint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCode, apiUrl]);

  const formatTimestamp = value => {
    if (!value) return '';
    return new Date(value * 1000).toISOString();
  };

  const openNewPromptEditor = () => {
    setHeadless(false);
    setSequence('');
    setErrorMessage('');
    setSelectedName('');
    setExpandedRevisionUuid('');
    setPromptName('');
    setPromptText('');
    setTargetOutcome(DEFAULT_TARGET_OUTCOME);
    setTags('');
    setShowEditor(true);
  };

  const openExistingPromptEditor = item => {
    const revisions = prompts[item.name] || [];
    const latest = revisions.length ? revisions[revisions.length - 1] : null;
    setHeadless(latest ? latest.headless === true : false);
    setSequence(latest && latest.sequence ? String(latest.sequence) : '');
    setErrorMessage('');
    setSelectedName(item.name);
    setExpandedRevisionUuid(latest && latest.revisionUuid ? latest.revisionUuid : '');
    setPromptName(item.name);
    setPromptText('');
    setTargetOutcome(DEFAULT_TARGET_OUTCOME);
    setTags('');
    setShowEditor(true);
  };

  const currentRevisions = useMemo(() => {
    if (!selectedName) return [];
    return [...(prompts[selectedName] || [])]
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  }, [prompts, selectedName]);

  const copyRevisionToForm = revision => {
    if (!revision) return;
    setHeadless(revision.headless === true);
    setSequence(revision.sequence ? String(revision.sequence) : '');
    setPromptText(revision.prompt || '');
    setTargetOutcome(revision.targetOutcome || DEFAULT_TARGET_OUTCOME);
    setTags(revision.tags || '');
  };

  const deleteRevision = revisionUuid => {
    if (!selectedName || !revisionUuid) return;
    setDeletingRevisionUuid(revisionUuid);
    setErrorMessage('');
    const deletingLastRevision = currentRevisions.length <= 1;
    const data = {
      token: customerCode,
      name: selectedName,
      revisionUuid
    };
    promptDeleteApi_(apiUrl).fetch({data})
      .then(() => {
        if (!isMounted.current) return;
        if (deletingLastRevision) {
          setShowEditor(false);
          setSelectedName('');
          setExpandedRevisionUuid('');
          setPromptName('');
          setPromptText('');
          setTargetOutcome(DEFAULT_TARGET_OUTCOME);
          setTags('');
        } else if (expandedRevisionUuid === revisionUuid) {
          setExpandedRevisionUuid('');
        }
        loadPrompts();
      })
      .catch(error => {
        if (!isMounted.current) return;
        errorLog_(error);
        setErrorMessage('Unable to delete revision.');
      })
      .finally(() => {
        if (!isMounted.current) return;
        setDeletingRevisionUuid('');
      });
  };

  const deletePrompt = (name, event) => {
    event.stopPropagation();
    if (!name) return;
    setDeletingPromptName(name);
    setErrorMessage('');
    const data = {
      token: customerCode,
      name
    };
    promptDeleteApi_(apiUrl).fetch({data})
      .then(() => {
        if (!isMounted.current) return;
        if (selectedName === name) {
          setShowEditor(false);
          setSelectedName('');
          setExpandedRevisionUuid('');
          setPromptName('');
          setPromptText('');
          setTargetOutcome(DEFAULT_TARGET_OUTCOME);
          setTags('');
        }
        loadPrompts();
      })
      .catch(error => {
        if (!isMounted.current) return;
        errorLog_(error);
        setErrorMessage('Unable to delete prompt.');
      })
      .finally(() => {
        if (!isMounted.current) return;
        setDeletingPromptName('');
      });
  };

  const submit = () => {
    const name = promptName.trim();
    const prompt = promptText;
    const normalizedTargetOutcome = (targetOutcome || '').trim() || DEFAULT_TARGET_OUTCOME;
    const normalizedTags = (tags || '').trim();
    const sequenceNumber = sequence === '' ? undefined : Number(sequence);
    if (sequenceNumber !== undefined && (!Number.isSafeInteger(sequenceNumber) || sequenceNumber < 1)) {
      setErrorMessage('Sequence must be a positive integer.');
      return;
    }
    if (!name || !prompt) {
      setErrorMessage('Name and prompt body are required.');
      return;
    }

    setSaving(true);
    setErrorMessage('');

    const data = {token: customerCode, name, prompt, targetOutcome: normalizedTargetOutcome, tags: normalizedTags, headless,
      ...(sequenceNumber === undefined ? {} : {sequence: sequenceNumber})};
    const isExisting = selectedName !== '' || Object.prototype.hasOwnProperty.call(prompts, name);
    const mutationApi = isExisting ? promptEditApi_ : promptSaveApi_;
    mutationApi(apiUrl).fetch({data})
      .then(() => {
        if (!isMounted.current) return;
        setShowEditor(false);
        setSelectedName('');
        setExpandedRevisionUuid('');
        setPromptName('');
        setPromptText('');
        setTargetOutcome(DEFAULT_TARGET_OUTCOME);
        setTags('');
        loadPrompts();
      })
      .catch(error => {
        if (!isMounted.current) return;
        errorLog_(error);
        setErrorMessage('Unable to save prompt.');
      })
      .finally(() => {
        if (!isMounted.current) return;
        setSaving(false);
      });
  };

  if (customerCode === '') {
    return null;
  }

  return (
    <div className="prompts-dashboard">
      <Card>
        <Card.Header className="bg-light">
          <Flex justifyContent="between" alignItems="center">
            <h5 className="mb-0">Prompt Library</h5>
            <Button className="prompt-add-button" variant="primary" onClick={openNewPromptEditor}>
              Add New Prompt
            </Button>
          </Flex>
        </Card.Header>
        <Card.Body>
          {errorMessage !== '' && !showEditor && (
            <Alert className="prompt-list-error mb-3" variant="danger">{errorMessage}</Alert>
          )}

          {loading && (
            <div className="prompts-loading d-flex justify-content-center py-4">
              <Spinner animation="border" role="status"/>
            </div>
          )}

          {!loading && !showEditor && listItems.length === 0 && (
            <div className="prompts-empty text-700">No prompts found yet.</div>
          )}

          {!loading && !showEditor && listItems.length > 0 && (
            <Row className="g-3">
              {listItems.map((item, index) => (
                <Col key={item.name} md={6} xl={4}>
                  <Card className={`prompt-card prompt-card-${index} h-100 cursor-pointer`} onClick={() => openExistingPromptEditor(item)}>
                    <Card.Body>
                      <Flex justifyContent="between" alignItems="start" className="mb-2">
                        <h6 className="prompt-name mb-0">{item.name}</h6>
                        <Button
                          type="button"
                          variant="danger"
                          className={`prompt-delete-prompt-button prompt-delete-prompt-button-${index} p-1`}
                          onClick={event => deletePrompt(item.name, event)}
                          disabled={deletingPromptName === item.name}
                          title="Delete Prompt"
                          aria-label="Delete Prompt"
                        >
                          {deletingPromptName === item.name ? 'Deleting...' : <FontAwesomeIcon icon="trash-alt"/>}
                        </Button>
                      </Flex>
                      <div className="small text-700 mb-1">
                        <span className="prompt-sequence">Sequence: {item.sequence || 'Unassigned'}</span>
                      </div>
                      <div className="small text-700 mb-1">
                        Current Revision:
                        <span className="prompt-current-revision ms-1">{item.latestRevisionUuid}</span>
                      </div>
                      <div className="small text-700 mb-1">
                        Revisions:
                        <span className="prompt-revision-count ms-1">{item.revisionCount}</span>
                      </div>
                      <div className="small text-700">
                        Latest:
                        <span className="prompt-latest-timestamp ms-1">{formatTimestamp(item.latestTimestamp)}</span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {!loading && showEditor && (
            <div className="prompt-editor">
              <Button
                variant="falcon-default"
                className="prompt-editor-back mb-3"
                onClick={() => setShowEditor(false)}
              >
                Back To Prompts
              </Button>

              {errorMessage && <Alert className="prompt-form-error" variant="danger">{errorMessage}</Alert>}

              {selectedName !== '' && (
                <Card className="prompt-revisions mb-3">
                  <Card.Header className="bg-light">Revisions</Card.Header>
                  <Card.Body>
                    {currentRevisions.length === 0 && (
                      <div className="text-700">No revisions available.</div>
                    )}
                    {currentRevisions.length > 0 && (
                      <div className="d-flex flex-column gap-2">
                        {currentRevisions.map((revision, index) => {
                          const revisionUuid = revision.revisionUuid || '';
                          const isExpanded = expandedRevisionUuid === revisionUuid;
                          return (
                            <Card
                              key={revision.revisionUuid || `${revision.timestamp}-${index}`}
                              className={`prompt-revision-item prompt-revision-item-${index}`}
                            >
                              <Button
                                type="button"
                                variant={isExpanded ? 'primary' : 'falcon-default'}
                                className="w-100 text-start rounded-0"
                                onClick={() => setExpandedRevisionUuid(isExpanded ? '' : revisionUuid)}
                              >
                                <div className="small text-start">
                                  <div className="prompt-revision-uuid">{revision.revisionUuid}</div>
                                  <div className="text-700">{formatTimestamp(revision.timestamp)}</div>
                                </div>
                              </Button>
                              {isExpanded && (
                                <Card.Body className="pt-3">
                                  <Form.Group className="mb-2" controlId={`selectedRevisionBody-${index}`}>
                                    <Form.Label>Revision (Read Only)</Form.Label>
                                    <Form.Control
                                      className="prompt-selected-revision-content"
                                      as="textarea"
                                      rows={8}
                                      value={revision.prompt || ''}
                                      readOnly
                                    />
                                  </Form.Group>
                                  <div className="small text-700 mb-2">
                                    Target Outcome:
                                    <span className="ms-1">{revision.targetOutcome || DEFAULT_TARGET_OUTCOME}</span>
                                  </div>
                                  <div className="small text-700 mb-2">
                                    Tags:
                                    <span className="prompt-revision-tags ms-1">{revision.tags || ''}</span>
                                    <div className="prompt-revision-sequence">Sequence: {revision.sequence || 'Unassigned'}</div>
                                    <div className="prompt-revision-headless">Headless: {revision.headless === true ? 'Yes' : 'No'}</div>
                                  </div>
                                  <Flex alignItems="center" className="gap-2">
                                    <Button
                                      type="button"
                                      variant="falcon-default"
                                      className="prompt-copy-revision-button"
                                      onClick={() => copyRevisionToForm(revision)}
                                    >
                                      Copy To New Revision
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="danger"
                                      className="prompt-delete-revision-button"
                                      onClick={() => deleteRevision(revisionUuid)}
                                      disabled={deletingRevisionUuid === revisionUuid}
                                      title="Delete Revision"
                                      aria-label="Delete Revision"
                                    >
                                      {deletingRevisionUuid === revisionUuid ? 'Deleting...' : <FontAwesomeIcon icon="trash-alt"/>}
                                    </Button>
                                  </Flex>
                                </Card.Body>
                              )}
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

              <Form>
                <Form.Group className="mb-3" controlId="promptName">
                  <Form.Label>Prompt Name</Form.Label>
                  <Form.Control
                    className="prompt-name-input"
                    type="text"
                    value={promptName}
                    onChange={event => setPromptName(event.target.value)}
                    disabled={selectedName !== ''}
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="promptBody">
                  <Form.Label>Prompt Body</Form.Label>
                  <Form.Control
                    className="prompt-content-input"
                    as="textarea"
                    rows={14}
                    value={promptText}
                    onChange={event => setPromptText(event.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="targetOutcome">
                  <Form.Label>Target Outcome</Form.Label>
                  <Form.Control
                    className="prompt-target-outcome-input"
                    type="text"
                    value={targetOutcome}
                    placeholder={DEFAULT_TARGET_OUTCOME}
                    onChange={event => setTargetOutcome(event.target.value)}
                  />
                  <Form.Text muted>
                    Data sources: count, sampled, aggregate, jsondb. Separate multiple sources with commas.
                    Aggregate data is included only when selected.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    id="promptHeadless"
                    className="prompt-headless-input"
                    type="checkbox"
                    label="Headless (JSON-only output)"
                    checked={headless}
                    onChange={event => setHeadless(event.target.checked)}
                  />
                  <Form.Text muted>Headless recommendations require jsondb as the only data source. HTML reports can combine jsondb with other sources.</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="promptSequence">
                  <Form.Label>Sequence</Form.Label>
                  <Form.Control
                    className="prompt-sequence-input"
                    type="number"
                    min="1"
                    step="1"
                    value={sequence}
                    onChange={event => setSequence(event.target.value)}
                  />
                  <Form.Text muted>Lower numbers run first. Leave blank to append a new prompt or keep its current position.</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="promptTags">
                  <Form.Label>Tags</Form.Label>
                  <Form.Control
                    className="prompt-tags-input"
                    type="text"
                    value={tags}
                    placeholder="marketing, revenue"
                    onChange={event => setTags(event.target.value)}
                  />
                  <Form.Text className="text-muted">Separate multiple tags with commas.</Form.Text>
                </Form.Group>

                <Button
                  className="prompt-save-button"
                  variant="primary"
                  onClick={submit}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : (selectedName !== '' ? 'Save Revision' : 'Create Prompt')}
                </Button>
              </Form>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

PromptDashboard.propTypes = {
  promptViewApi_: PropTypes.func,
  promptSaveApi_: PropTypes.func,
  promptEditApi_: PropTypes.func,
  promptDeleteApi_: PropTypes.func,
  errorLog_: PropTypes.func
};

export default PromptDashboard;
